import * as React from "react";

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { axiosInstance } from "@needl-ai/common/lib/axios";

import type { AskNeedlTools } from "@/types/api";
import type { Message } from "@/types/chat";

const SESSION_QUERY_KEY = "askneedl-session-by-id";
const ALL_SESSIONS_QUERY_KEY = "askneedl-sessions";
const ML_API_ROUTES = {
  askNeedlSessionById: (sessionId: string) =>
    `/ml/ask-needl/sessions/${sessionId}`,
  askNeedlSessions: () => `/ml/ask-needl/sessions`,
};

export const useFetchAllSession = ({
  q,
  askneedl_type,
}: FetchAllSessionParams) => {
  return useInfiniteQuery({
    queryKey: [ALL_SESSIONS_QUERY_KEY, askneedl_type, q],
    queryFn: ({ pageParam = 0 }) =>
      axiosInstance.get<AllSessionsResponseType>(
        ML_API_ROUTES.askNeedlSessions(),
        {
          params: {
            q,
            page: pageParam,
            askneedl_type,
          },
        }
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage?.data?.pagination?.hasMore
        ? lastPage.data.pagination.page + 1
        : undefined,
    select: (data) => data.pages.flatMap((page) => page.data.sessions),
  });
};

export const useFetchSessionById = (sessionId?: string | null) => {
  return useQuery({
    queryKey: [SESSION_QUERY_KEY, sessionId],
    queryFn: async () => {
      const response = await axiosInstance.get<{ session: SessionDetails }>(
        ML_API_ROUTES.askNeedlSessionById(sessionId as string)
      );
      return response;
    },
    enabled: !!sessionId,
    staleTime: Infinity,
    gcTime: Infinity,
    select: (data) => data?.data?.session,
  });
};

export const useCreateUpdateSession = (
  askNeedlType: AskNeedlType = "SUPERCHARGING_ASKNEEDL"
) => {
  const invalidateSession = useInvalidateSession();
  const invalidateAllSessionsInfos = useInvalidateAllSessionsInfos();

  return useMutation({
    mutationFn: async ({
      sessionId,
      sessionState,
      title,
    }: UseCreateUpdateSessionType) => {
      const resolvedType: AskNeedlType = sessionState.report_type
        ? "ARTIFACT_REPORT"
        : askNeedlType;

      await axiosInstance.put(ML_API_ROUTES.askNeedlSessionById(sessionId), {
        title: title || sessionState.messages[0]?.text || "Untitled Session",
        state: {
          messages: sessionState.messages,
          tools: sessionState.tools,
          attachment_feed_id: sessionState.attachment_feed_id,
          report_type: sessionState.report_type,
          is_artifact_report_scheduling_message_disabled:
            sessionState.is_artifact_report_scheduling_message_disabled,
        },
        askneedl_type: resolvedType,
      });
    },
    onSuccess: async (_, { sessionId, skipSessionInvalidation }) => {
      await invalidateAllSessionsInfos();
      if (!skipSessionInvalidation) {
        await invalidateSession(sessionId);
      }
    },
  });
};

export const usePrepopulateSession = () => {
  const queryClient = useQueryClient();

  return React.useCallback(
    (sessionId: string, initialData: SessionDetails) => {
      queryClient.setQueryData([SESSION_QUERY_KEY, sessionId], {
        data: initialData,
      });
    },
    [queryClient]
  );
};

export const useDeleteSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sessionId: string) => {
      await axiosInstance.delete(ML_API_ROUTES.askNeedlSessionById(sessionId));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ALL_SESSIONS_QUERY_KEY],
        refetchType: "all",
      });
    },
  });
};

export const useInvalidateAllSessionsInfos = () => {
  const queryClient = useQueryClient();

  return React.useCallback(() => {
    return queryClient.invalidateQueries({
      queryKey: [ALL_SESSIONS_QUERY_KEY],
      refetchType: "all",
    });
  }, [queryClient]);
};

export const useInvalidateSession = () => {
  const queryClient = useQueryClient();

  return React.useCallback(
    (sessionId: string) => {
      return queryClient.invalidateQueries({
        queryKey: [SESSION_QUERY_KEY, sessionId],
        refetchType: "all",
      });
    },
    [queryClient]
  );
};

export type SessionInfo = {
  id: string;
  title: string;
  updated_at: string;
  matched_sentence?: string;
};

export type SessionDetails = SessionInfo & {
  state: {
    messages: Message[];
    tools: AskNeedlTools;
    attachment_feed_id?: string | null;
    report_type?: string;
    is_artifact_report_scheduling_message_disabled?: boolean;
  };
  askneedl_type: AskNeedlType;
};

type UseCreateUpdateSessionType = {
  sessionId: string;
  sessionState: {
    messages: Message[];
    tools: AskNeedlTools;
    attachment_feed_id?: string | null;
    report_type?: string;
    is_artifact_report_scheduling_message_disabled?: boolean;
  };
  title?: string;
  skipSessionInvalidation?: boolean;
};

type AllSessionsResponseType = {
  sessions: SessionInfo[];
  pagination?: {
    page: number;
    limit: number;
    hasMore: boolean;
  };
};

type AskNeedlType =
  | "SUPERCHARGING_ASKNEEDL"
  | "DEFAULT_ASKNEEDL"
  | "FINANCIAL_METRICS"
  | "ARTIFACT_REPORT";

type FetchAllSessionParams = {
  q?: string;
  askneedl_type?: AskNeedlType;
};
